// The fake Stripe page (mock only).
//
// It exists so the full-page redirect is real: state is wiped, the URL changes,
// and the flow has to rebuild itself from the session on the way back — exactly
// what happens against Stripe. A modal pretending to be a checkout would hide
// the one class of bug this journey actually has.
//
// Watermarked, and it refuses to render outside the mock API. Nothing here
// touches a card network; the outcome is whatever the demo picks.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import { isMockApi, MockCheckoutOutcome } from "../../../shared/api/submission";
import { readStoredCheckout } from "../../../models/submissionStep4Model";
import { trackSubmissionEvent } from "../analytics";
import { membershipPrimary } from "../common/membershipButton";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const OUTCOMES: { value: MockCheckoutOutcome; label: string }[] = [
  { value: "success", label: "Card accepted" },
  { value: "card_declined", label: "Card declined" },
  { value: "abandoned_3ds", label: "3DS abandoned (cancel)" },
  { value: "session_expired", label: "Checkout session expired" },
];

const RETURN_PATH = "/submission/checkout/return";

const MockStripeCheckoutPage: React.FC<Props> = ({
  history,
  location,
  completeMockCheckout,
}) => {
  const [outcome, setOutcome] = useState<MockCheckoutOutcome>("success");
  const [busy, setBusy] = useState(false);

  // The session id survives in the URL and in sessionStorage: the URL is what
  // Stripe would hand back, the storage is the belt to its braces.
  const params = new URLSearchParams(location.search);
  const stored = readStoredCheckout();
  const checkoutSessionId = params.get("session_id") || (stored && stored.checkoutSessionId);

  useEffect(() => {
    if (!isMockApi()) {
      // A real build has no business here — the real checkout is offsite.
      history.replace("/submission/flow/step/4");
      return;
    }
    trackSubmissionEvent("sf_step4_mock_checkout_viewed", { checkoutSessionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!checkoutSessionId) {
    return (
      <SubmissionLayout>
        <Watermark>{i18next.t("MOCK CHECKOUT — no real charge")}</Watermark>
        <Panel>
          <PanelTitle>{i18next.t("We couldn't find that checkout session")}</PanelTitle>
          <PanelText>
            {i18next.t("Start again from the offer and we'll open a fresh one.")}
          </PanelText>
          <PrimaryButton onClick={() => history.replace("/submission/paywall")}>
            {i18next.t("Return to the offer")}
          </PrimaryButton>
        </Panel>
      </SubmissionLayout>
    );
  }

  const onPay = async () => {
    if (busy) return;
    setBusy(true);
    const result = await completeMockCheckout({ checkoutSessionId, outcome });
    setBusy(false);

    // A declined card or an expired session rejects, and the model has already
    // stored the reason — the return page reads it and routes back to the
    // paywall with the failure modal.
    const status = result ? "success" : "cancel";
    history.replace(
      `${RETURN_PATH}?session_id=${encodeURIComponent(checkoutSessionId)}&status=${status}`
    );
  };

  return (
    <SubmissionLayout>
      <Watermark>{i18next.t("MOCK CHECKOUT — no real charge")}</Watermark>

      <Panel>
        <PanelTitle>theWorkbook PRO</PanelTitle>
        <PanelText>{i18next.t("Free for 30 days, then billed automatically.")}</PanelText>

        {/* Real autocomplete tokens so the browser's own card autofill can be
            tested here, which is where it actually matters. */}
        <Field
          placeholder={i18next.t("Card number")}
          aria-label={i18next.t("Card number")}
          autoComplete="cc-number"
          defaultValue="4242 4242 4242 4242"
        />
        <Row>
          <Field
            placeholder={i18next.t("MM / YY")}
            aria-label={i18next.t("Expiry")}
            autoComplete="cc-exp"
            defaultValue="12 / 30"
          />
          <Field
            placeholder={i18next.t("CVC")}
            aria-label={i18next.t("CVC")}
            autoComplete="cc-csc"
            defaultValue="123"
          />
        </Row>

        <OutcomeLabel htmlFor="mock-outcome">
          {i18next.t("What should happen?")}
        </OutcomeLabel>
        <Select
          id="mock-outcome"
          value={outcome}
          onChange={(e: any) => setOutcome(e.target.value)}
        >
          {OUTCOMES.map((option) => (
            <option key={option.value} value={option.value}>
              {i18next.t(option.label)}
            </option>
          ))}
        </Select>

        <PrimaryButton onClick={onPay} disabled={busy}>
          {busy ? i18next.t("One moment…") : i18next.t("Start trial")}
        </PrimaryButton>
        <TextButton
          type="button"
          onClick={() =>
            history.replace(
              `${RETURN_PATH}?session_id=${encodeURIComponent(
                checkoutSessionId
              )}&status=cancel`
            )
          }
          disabled={busy}
        >
          {i18next.t("Cancel and go back")}
        </TextButton>
      </Panel>
    </SubmissionLayout>
  );
};

const Watermark = styled.div`
  margin: 12px 0;
  padding: 8px 10px;
  border: 1px dashed #c7053f;
  border-radius: 6px;
  color: #c7053f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: center;
`;

const Panel = styled.div`
  border: 1px solid #e3e8ee;
  border-radius: 10px;
  padding: 16px;
`;

const PanelTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 4px;
`;

const PanelText = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-bottom: 14px;
`;

const Field = styled.input`
  width: 100%;
  height: 46px;
  margin-bottom: 10px;
  padding: 0 12px;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  font-size: 15px;
  color: #122640;
  box-sizing: border-box;
  outline: 0;

  &:focus {
    border-color: #0f9bf0;
  }
`;

const Row = styled.div`
  display: flex;

  input:first-child {
    margin-right: 10px;
  }
`;

const OutcomeLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #53627c;
  margin: 6px 0 4px;
`;

const Select = styled.select`
  width: 100%;
  height: 46px;
  margin-bottom: 14px;
  padding: 0 10px;
  border: 1px solid #c9d3e0;
  border-radius: 5px;
  background: #ffffff;
  font-size: 15px;
  color: #122640;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 0;
  cursor: pointer;
  min-height: 44px;
`;

interface StateProps {
  checkoutSessionIdInStore: string | null;
}

interface DispatchProps {
  completeMockCheckout: (payload: {
    checkoutSessionId: string;
    outcome: MockCheckoutOutcome;
  }) => Promise<any>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  checkoutSessionIdInStore: state.submissionStep4Model.checkoutSessionId,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  completeMockCheckout: dispatch.submissionStep4Model.completeMockCheckout,
});

export default connect(mapStateToProps, mapDispatchToProps)(MockStripeCheckoutPage);
